<template>
  <div class="d-inline-flex align-center" v-if="$can('create', 'Contact')">
    <v-btn 
      v-if="action == 'create'" 
      color="primary" 
      outlined 
      depressed 
      small 
      @click="showDialog = true"
    >
      <v-icon x-small class="mr-2">fas fa-plus</v-icon>
      Agregar Contacto
    </v-btn>
    
    <v-btn 
      v-if="action == 'edit'" 
      icon 
      x-small 
      color="primary" 
      @click="actionEdit"
      class="mx-1"
    >
      <v-icon x-small>fas fa-edit</v-icon>
    </v-btn>

    <v-dialog v-model="showDialog" max-width="500" persistent>
      <v-card outlined class="modern-card overflow-hidden">
        <v-sheet color="grey lighten-4" class="px-6 py-4 border-bottom">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-3">
              {{ action == 'create' ? 'fas fa-user-plus' : 'fas fa-user-edit' }}
            </v-icon>
            <div>
              <div class="text-h6 font-weight-bold lh-1">
                {{ action == 'create' ? 'Crear Contacto' : 'Editar Contacto' }}
              </div>
              <div class="caption grey--text mt-1">
                {{ action == 'create' ? 'Asignar nuevo personal al cliente' : 'Actualizar datos de contacto' }}
              </div>
            </div>
          </div>
        </v-sheet>

        <v-card-text class="pa-6 pt-8">
          <ValidationObserver v-slot="{ handleSubmit }">
            <v-form @submit.prevent="handleSubmit(submitContact)">
              <v-row dense>
                <v-col cols="12" md="6">
                  <ValidationProvider name="Nombre" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="first_name"
                      label="Nombres"
                      outlined
                      dense
                      placeholder="Ej: Juan"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" md="6">
                  <ValidationProvider name="Apellido" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="last_name"
                      label="Apellidos"
                      outlined
                      dense
                      placeholder="Ej: Pérez"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12">
                  <ValidationProvider name="Email" rules="required|email" v-slot="{ errors }">
                    <v-text-field
                      v-model="email"
                      label="Correo Electrónico"
                      outlined
                      dense
                      placeholder="juan@empresa.com"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" md="6">
                  <ValidationProvider name="Teléfono" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="phone"
                      label="Teléfono"
                      outlined
                      dense
                      placeholder="+56 9 ..."
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" md="6">
                  <ValidationProvider name="Cargo" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="cargo"
                      label="Cargo / Función"
                      outlined
                      dense
                      placeholder="Ej: Gerente"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>

              <v-divider class="my-6"></v-divider>

              <div class="d-flex justify-space-between align-center px-2">
                <div>
                  <v-btn 
                    v-if="action == 'edit'" 
                    text 
                    color="error" 
                    small 
                    @click="showConfirmDelete = true"
                    class="px-0"
                  >
                    <v-icon x-small class="mr-1">fas fa-trash-alt</v-icon>
                    Eliminar
                  </v-btn>
                </div>
                <div class="d-flex">
                  <v-btn text small @click="cancel()" class="mr-2" :disabled="loading">
                    Cancelar
                  </v-btn>
                  <v-btn 
                    type="submit" 
                    color="primary" 
                    depressed 
                    small 
                    class="px-6"
                    :loading="loading"
                  >
                    <v-icon x-small class="mr-2">fas fa-save</v-icon>
                    {{ action == 'create' ? 'Registrar' : 'Actualizar' }}
                  </v-btn>
                </div>
              </div>
            </v-form>
          </ValidationObserver>
        </v-card-text>
      </v-card>
    </v-dialog>

    <confirm
      v-model="showConfirmDelete" 
      message="¿Confirma eliminar este contacto? Esta operación no se puede deshacer."
      @ok="deleteContact"
      @cancel="showConfirmDelete = false"
    />
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import { createContact, updateContact, deleteContact } from '@/api/customers'

export default {
  name: 'ContactCreateUpdate2',
  props: {
    customerId: {
      type: Number,
      required: true,
      default: null
    },
    value: {
      type: Object,
      default: null
    },
    action: {
      type: String,
      default: 'create'
    }
  },
  data: () => ({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    cargo: '',
    showDialog: false,
    showConfirmDelete: false,
    loading: false
  }),
  methods: {
    submitContact() {
      this.loading = true;
      if (this.action == 'create') this.addContact();
      else if (this.action == 'edit') this.editContact();
    },
    async addContact() {
      if (!this.customerId) {
        notifications.showError('Error', 'ID de cliente no válido');
        this.loading = false;
        return;
      }
      let contact = {
        first_name: this.first_name,
        last_name: this.last_name,
        email: this.email,
        phone: this.phone,
        cargo: this.cargo,
        customer: this.customerId
      }
      await createContact(contact)
      .then(() => {
        notifications.showSuccess('Contacto creado correctamente');
        this.$emit('created', contact);
        this.cancel();
      })
      .catch((e) => {
        notifications.showError('Error al crear el contacto: ' + e);
      })
      .finally(() => {
        this.loading = false;
      });
    },
    async editContact() {
      let contact = {
        id: this.value.id,
        first_name: this.first_name,
        last_name: this.last_name,
        email: this.email,
        phone: this.phone,
        cargo: this.cargo,
        customer: this.customerId
      }
      await updateContact(contact)
      .then(() => {
        notifications.showSuccess('Contacto actualizado correctamente');
        this.$emit('updated', contact);
        this.cancel();
      })
      .catch((e) => {
        notifications.showError('Error al actualizar el contacto: ' + e);
      })
      .finally(() => {
        this.loading = false;
      });
    },
    actionEdit() {
      this.first_name = this.value.first_name;
      this.last_name = this.value.last_name;
      this.email = this.value.email;
      this.phone = this.value.phone;
      this.cargo = this.value.cargo;
      this.showDialog = true;
    },
    async deleteContact() {
      this.loading = true;
      await deleteContact({ id: this.value.id })
      .then(() => {
        notifications.showSuccess('Contacto eliminado correctamente');
        this.$emit('deleted', this.value);
        this.cancel();
      })
      .catch((e) => {
        notifications.showError('Error al eliminar el contacto: ' + e);
      })
      .finally(() => {
        this.loading = false;
      });
    },
    cancel() {
      this.showDialog = false;
      this.showConfirmDelete = false;
      if (this.action == 'create') {
        this.first_name = '';
        this.last_name = '';
        this.email = '';
        this.phone = '';
        this.cargo = '';
      }
      this.loading = false;
    }
  }
}
</script>

<style scoped>
.modern-card {
  border: 1px solid rgba(0,0,0,0.08) !important;
}
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.lh-1 { line-height: 1.2; }
</style>